import { useMemo, type MutableRefObject } from "react";

import type { TransferInvestment } from "../types";

type Row = Pick<TransferInvestment, "id" | "isIncluded" | "sourceInvestmentId">;

type Attachment = {
  investmentId?: string;
};

type Retargeted<T> = T & {
  targetInvestmentId: string;
};

type Options<R extends Row, F extends Attachment, T extends Attachment> = {
  table: MutableRefObject<{ getDataSource?: () => R[] }>;
  sourceFiles: F[];
  customTargets: unknown[];
  sourceProjectId: string;
  targetProjectId?: string;
  sourceInvestments: unknown[];
  targetInvestments: unknown[];
  sourceTransactions: T[];
};

type TransferItems<R extends Row, F extends Attachment, T extends Attachment> = {
  filesToMove: F[];
  filesToOverride: Retargeted<F>[];
  investmentsToMove: R[];
  transactionsToMove: T[];
  investmentsToOverride: R[];
  transactionsToOverride: Retargeted<T>[];
};

const useTransferItems = <R extends Row, F extends Attachment, T extends Attachment>({
  table,
  sourceFiles,
  customTargets,
  sourceProjectId,
  targetProjectId,
  sourceInvestments,
  targetInvestments,
  sourceTransactions,
}: Options<R, F, T>): TransferItems<R, F, T> => {
  const investmentsToMove = useMemo(() =>
    table.current.getDataSource()
      .filter(({ isIncluded }) => isIncluded)
      .filter(({ sourceInvestmentId }) => !sourceInvestmentId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , [ table, sourceInvestments, targetInvestments, targetProjectId, sourceProjectId, customTargets ]);

  const investmentsToOverride = useMemo(() =>
    table.current.getDataSource()
      .filter(({ isIncluded }) => isIncluded)
      .filter(({ sourceInvestmentId }) => !!sourceInvestmentId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , [ table, sourceInvestments, targetInvestments, targetProjectId, sourceProjectId, customTargets ]);

  const filesToMove = useMemo(() =>
    investmentsToMove
      .map(({ id }) =>
        sourceFiles
          .filter((file) => file.investmentId === id),
      )
      .reduce((cur, acc) => [ ...acc, ...cur ], [] as F[])
  , [ investmentsToMove, sourceFiles ]);

  const filesToOverride = useMemo(() =>
    investmentsToOverride
      .map(({ sourceInvestmentId, id: targetInvestmentId }) =>
        sourceFiles
          .filter((file) => file.investmentId === sourceInvestmentId)
          .map((file) => ({ ...file, targetInvestmentId })),
      )
      .reduce((cur, acc) => [ ...acc, ...cur ], [] as Retargeted<F>[])
  , [ investmentsToOverride, sourceFiles ]);

  const transactionsToMove = useMemo(() =>
    investmentsToMove
      .map(({ id }) =>
        sourceTransactions.filter((transaction) => transaction.investmentId === id),
      )
      .reduce((cur, acc) => [ ...acc, ...cur ], [] as T[])
  , [ investmentsToMove, sourceTransactions ]);

  const transactionsToOverride = useMemo(() =>
    investmentsToOverride
      .map(({ sourceInvestmentId, id: targetInvestmentId }) =>
        sourceTransactions
          .filter((transaction) => transaction.investmentId === sourceInvestmentId)
          .map((transaction) => ({ ...transaction, targetInvestmentId })),
      )
      .reduce((cur, acc) => [ ...acc, ...cur ], [] as Retargeted<T>[])
  , [ investmentsToOverride, sourceTransactions ]);

  return {
    investmentsToMove,
    investmentsToOverride,
    filesToMove,
    filesToOverride,
    transactionsToMove,
    transactionsToOverride,
  };
};

export default useTransferItems;
